import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header/Header';
import LandingAppStore from '../features/apps/components/LandingAppStore';
import { useAuth } from '../features/auth/hooks/useAuth';
import EduLandingPage from '../features/edu-landing/EduLandingPage';
import MarketingLayout from '../features/marketing/MarketingLayout';
import IvoriLandingPage from '../features/ivori-landing/IvoriLandingPage';
import CSuiteLandingPage from '../features/csuite-landing/CSuiteLandingPage';
import NotFoundPage from '../components/NotFoundPage/NotFoundPage';
import { isEduPortal, isPathbuddyHost } from '../config/eduHost';
import { isIvoriApexHost, isCSuiteHost } from '../config/ivoriHost';
import { resolvePageTitle, resolvePageDescription } from '../config/pageTitles';
import useRetryPendingHandoffClaim from '../features/edu-landing/useRetryPendingHandoffClaim';
import './app.css';

const MarketingHomePage = React.lazy(() => import('../features/marketing/pages/HomePage'));

// Home page component: app-store style landing, all clicks redirect to sign-in
const HomePage = ({ onNavigate }) => {
  return (
    <>
      <Header onNavigate={onNavigate} variant="landing" />
      <main>
        <LandingAppStore onNavigate={onNavigate} />
      </main>
    </>
  );
};

// Helper to determine exam key from hostname
const getSubdomainExamKey = (hostname) => {
  const h = (hostname || '').toLowerCase();
  if (h.startsWith('lnat') || h.includes('lnat.')) return 'lnat';
  if (h.startsWith('tmua') || h.includes('tmua.')) return 'tmua';
  if (h.startsWith('ucat') || h.includes('ucat.')) return 'ucat';
  if (h.startsWith('lsat') || h.includes('lsat.')) return 'lsat';
  if (h.startsWith('tara') || h.includes('tara.')) return 'tara';
  if (h.startsWith('esat') || h.includes('esat.')) return 'esat';
  if (h.startsWith('astonmmi') || h.startsWith('ammi') || h.includes('astonmmi.')) return 'astonmmi';
  if (h.startsWith('birminghammmi') || h.startsWith('bmmi') || h.includes('birminghammmi.')) return 'birminghammmi';
  if (h.startsWith('cmp') || h.startsWith('cambridgemedicalpanel') || h.includes('cmp.')) return 'cmp';
  if (h.startsWith('oxbridge') || h.includes('oxbridge.')) return 'oxbridge';
  if (h.startsWith('medicalschool') || h.includes('medicalschool.')) return 'medicalschool';
  if (h.startsWith('degreeapprenticeship') || h.includes('degreeapprenticeship.')) return 'degree_apprenticeships';
  return null;
};

// Main App content component
const AppContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useRetryPendingHandoffClaim();

  const searchParams = new URLSearchParams(location.search);
  const queryExam = searchParams.get('exam');
  const hostname = window.location.hostname.toLowerCase();
  const matchedExam = (queryExam || '').toLowerCase() || getSubdomainExamKey(hostname);

  // Handle page navigation instantly without motion delays
  const handleNavigate = (page) => {
    const routes = {
      login: '/login',
      home: '/',
      dashboard: '/apps',
      apps: '/apps'
    };
    
    const route = routes[page];
    if (route) {
      navigate(route);
    }
  };

  // Update page title and description dynamically based on exact route and subdomain
  useEffect(() => {
    const title = resolvePageTitle(location.pathname, hostname);
    const description = resolvePageDescription(location.pathname, hostname);
    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', description);
    }
  }, [location.pathname, matchedExam, hostname]);

  // Scroll to top on route change without animations
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  // The company home lives at ivoriai.com/about; ivoriai.com/ is the Edu
  // home. /about anywhere else, including ivoriai.com/edu/about, is not a page.
  if (location.pathname === '/about') {
    if (!matchedExam && !isEduPortal(hostname) && isIvoriApexHost(hostname)) {
      return (
        <div className="App">
          <div className="page-content">
            <IvoriLandingPage />
          </div>
        </div>
      );
    }
    return <NotFoundPage />;
  }

  // Exam-subdomain "/" no longer renders a landing here. The host map
  // (index.html, with the router fallback) sends the eight prototype exams
  // to ivoriai.com/edu/<exam>. STEP, LSAT, TSA and the MMI hosts are unchanged.
  // Under the /edu basename this "/" is the edu portal home: ivoriai.com/
  // (router "/edu" via rootAliasHistory) or localhost:3000/edu. The company
  // home is ivoriai.com/about with no basename; the apex branch below is only
  // reached by ivori.ai, which the host map sends to ivoriai.com/.
  if (location.pathname === '/') {
    if (isPathbuddyHost(hostname)) {
      return (
        <div className="App">
          <div className="page-content">
            <EduLandingPage />
          </div>
        </div>
      );
    }
    if (isEduPortal(hostname) && !getSubdomainExamKey(hostname)) {
      return (
        <MarketingLayout>
          <Suspense fallback={<div role="status">Loading</div>}>
            <MarketingHomePage />
          </Suspense>
        </MarketingLayout>
      );
    }
    if (!matchedExam && isCSuiteHost(hostname)) {
      return (
        <div className="App">
          <div className="page-content">
            <CSuiteLandingPage />
          </div>
        </div>
      );
    }
    if (!matchedExam && !isEduPortal(hostname) && isIvoriApexHost(hostname)) {
      return (
        <div className="App">
          <div className="page-content">
            <IvoriLandingPage />
          </div>
        </div>
      );
    }
  }

  return (
    <div className="App">
      <div className="page-content">
        <HomePage onNavigate={handleNavigate} />
      </div>
    </div>
  );
};

export default AppContent;